import { Antrag, Beleg, Seite } from '../types';
import { AntragsLimitService } from '@eBeihilfe/einreichung-shared/services';
import { ConfigSettingsService, Deferred } from '@ebeihilfe/core';
import { Directive, OnInit, inject } from '@angular/core';
import { EinreichungStep } from '../enums';
import { EinreichungStepResultType } from '../interfaces';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';

@Directive()
export abstract class BelegSelectionBase implements OnInit {
  public antrag?: Antrag;
  public dailyLimitExceeded?: Observable<boolean>;
  public maximumAntragSizeInMb = 100;
  public maximumFileSizeInMb = 20;
  public prio = EinreichungStep.MAIN;

  protected readonly antragsLimitService = inject(AntragsLimitService);
  protected readonly configService = inject(ConfigSettingsService);
  protected readonly translate = inject(TranslateService);

  protected currentActiveBeleg?: Beleg;
  protected minimumSeiteSizeInByte = 6000;
  protected work?: Deferred<EinreichungStepResultType>;

  private _customerHasUnfinishedAntragsFeature = false;
  private _firstBelegIsMandatory = true;
  private _mandatoryPages = 0;

  public get customerHasUnfinishedAntragsFeature(): boolean {
    return this._customerHasUnfinishedAntragsFeature;
  }

  public get firstBelegIsMandatory(): boolean {
    return this._firstBelegIsMandatory && !!this.antrag?.isLangantrag;
  }

  public get mandatoryPages(): number {
    return this._mandatoryPages;
  }

  public canDeactivate(): boolean {
    const hasPendingData = this.antrag?.hasPendingData;

    if (!hasPendingData) {
      return true;
    }

    const answer: boolean = confirm(this.translate.instant(_('new_antrag.text.discarding_all_data')));
    if (answer && this.work) {
      this.work.resolve({
        result: undefined,
        shouldProceed: false,
      });
    }

    return answer;
  }

  public destroy(): Promise<void> {
    return Promise.resolve();
  }

  public finishBelegSelection(): void {
    this.work?.resolve({
      result: this.antrag,
      shouldProceed: true,
    });
  }

  public finishStep(): void {
    this.work?.resolve({
      result: this.antrag,
      shouldProceed: true,
    });
  }

  public hasUi(): boolean {
    return true;
  }

  public moveBelegToTop(beleg: Beleg): void {
    this.antrag?.moveBelegToTop(beleg);
  }

  public ngOnInit(): void {
    const { constants, customizing } = this.configService.appSettings;
    this._firstBelegIsMandatory = customizing.firstBelegMandatoryConfig.isActive;
    this._mandatoryPages = customizing.firstBelegMandatoryConfig.mandatoryPages;
    this._customerHasUnfinishedAntragsFeature = customizing.hasUnfinishedAntrage;
    this.maximumFileSizeInMb = constants.maximumFileSizeInMb;
    this.maximumAntragSizeInMb = constants.maximumAntragSizeInMb;

    this.dailyLimitExceeded = this.antragsLimitService.isLimitReached();
  }

  public removeSeiteFromAntrag(beleg: Beleg, seite: Seite): void {
    beleg.removeSeite(seite);
  }
}
